/* eslint-disable camelcase, no-inline-comments, quote-props, space-before-function-paren, brace-style,
object-curly-spacing, arrow-parens, no-multiple-empty-lines, prefer-template, no-trailing-spaces */
import loader from 'little-loader';
import Promise from 'bluebird';

// Event categories
const CATEGORY_CLIENT_ERROR = 'Client error';
const CATEGORY_API_ERROR    = 'Api error';
const CATEGORY_SESSION      = 'Session';
const CATEGORY_CALENDAR     = 'Calendar';
const CATEGORY_DASHBOARD    = 'Dashboard';
const CATEGORY_CATEGORY     = 'Category';
const CATEGORY_TRANSACTION  = 'Transaction';
const CATEGORY_PROFILE      = 'Profile';
const CATEGORY_SUBSCRIPTION = 'Subscription';
const CATEGORY_MODAL        = 'Modal';
const CATEGORY_USER         = 'User';

export class GoogleAnalytics {
    constructor(measurementId, googleAdsId, version) {
        this.measurementId = measurementId; // GA4 measurement ID (G-XXXXXXXX)
        this.googleAdsId = googleAdsId || null; // Google Ads ID (AW-XXXXXXXXXX)
        this.version = version;
    }

    getInstance() {
        if (!this.promise) {
            this.promise = new Promise((resolve, reject) => {
                if (!this.measurementId) {
                    return reject(new Error('Google Analytics disabled. No measurement ID provided.'));
                }

                // Initialize dataLayer
                window.dataLayer = window.dataLayer || [];
                window.gtag = function() { window.dataLayer.push(arguments); };
                
                // Load gtag.js
                loader(`https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`, (error) => {
                    if (error) {
                        return reject(error);
                    }
                    
                    this.initialize();
                    return resolve(window.gtag);
                });
            });
        }

        return this.promise;
    }

    initialize() {
        const gtag = window.gtag;
        
        // Basic initialization
        gtag('js', new Date());
        
        // Configure the main measurement ID
        gtag('config', this.measurementId, {
            send_page_view: false, // Manual page view tracking
            custom_map: {
                'dimension1': 'client_id',
                'dimension4': 'app_version'
            }
        });
        
        // If Google Ads ID is provided, configure it too
        if (this.googleAdsId) {
            gtag('config', this.googleAdsId, {
                send_page_view: false
            });
        }
        
        // Set global parameters
        gtag('set', {
            'app_version': this.version
        });
        
        // Get client ID for dimension1
        if (typeof gtag.get === 'function') {
            gtag.get(this.measurementId, 'client_id', (clientId) => {
                gtag('set', {'client_id': clientId});
            });
        }
    }

    trackEvent(eventCategory, eventAction, eventLabel, eventValue, options = {}) {
        return this.getInstance().then(() => {
            const eventParams = {
                event_category: eventCategory,
                event_label: eventLabel || '',
                value: eventValue || 0,
                non_interaction: options && options.nonInteraction || false
            };
            
            // Add all properties from options to eventParams without using spread operator
            if (options) {
                Object.keys(options).forEach(key => {
                    if (key !== 'nonInteraction') { // Skip this as it's already handled above
                        eventParams[key] = options[key];
                    }
                });
            }
            
            window.gtag('event', eventAction, eventParams);
        }).catch(() => null);
    }

    trackPageView(page) {
        return this.getInstance().then(() => {
            window.gtag('event', 'page_view', {
                page_path: page
            });
        }).catch(() => null);
    }

    trackClientError(message) {
        return this.trackEvent(CATEGORY_CLIENT_ERROR, 'error', message, null, {
            non_interaction: true
        });
    }

    trackApiError(status, error, message, method, url) {
        return this.trackEvent(CATEGORY_API_ERROR, `${status}`, `[${error || 'UNKNOWN'}] ${message} @ ${method} ${url}`, null, {
            non_interaction: true
        });
    }

    trackSessionEnd(reason, interactive) {
        return this.trackEvent(CATEGORY_SESSION, 'logout', reason, null, {
            non_interaction: !interactive
        });
    }

    trackSignUp() {
        // Special handling for sign-up as conversion
        this.trackEvent(CATEGORY_USER, 'sign-up');
        
        // Track as conversion in Google Ads if configured
        if (this.googleAdsId) {
            window.gtag('event', 'conversion', {
                'send_to': this.googleAdsId + '/user_signup'
            });
        }
    }

    trackPurchaseConversion(transactionId, value, currency = 'RON') {
        return this.getInstance().then(() => {
            if (!this.googleAdsId) {
                return null;
            }
            
            // Track the purchase as a conversion in Google Ads
            window.gtag('event', 'conversion', {
                'send_to': this.googleAdsId + '/2VkTCNnftKYaEIvGk_8-',
                'value': value || 1.0,
                'currency': currency || 'RON',
                'transaction_id': transactionId || ''
            });
            
            // Also track in our standard event system
            this.trackEvent(CATEGORY_SUBSCRIPTION, 'purchase', `Transaction: ${transactionId}`, value, {
                currency,
                transaction_id: transactionId,
                'non_interaction': false
            });
        }).catch(() => null);
    }

    trackCalendarSpecificMonthSelect() {
        return this.trackEvent(CATEGORY_CALENDAR, 'select-month', 'Specific');
    }

    trackCalendarPreviousMonthSelect() {
        return this.trackEvent(CATEGORY_CALENDAR, 'select-month', 'Previous');
    }

    trackCalendarNextMonthSelect() {
        return this.trackEvent(CATEGORY_CALENDAR, 'select-month', 'Next');
    }

    trackCalendarTodaySelect() {
        return this.trackEvent(CATEGORY_CALENDAR, 'select-month', 'Today');
    }

    trackCategoryCreate(category, from) {
        return this.trackEvent(CATEGORY_CATEGORY, 'create', `From ${from}`);
    }

    trackCategoryUpdate() {
        return this.trackEvent(CATEGORY_CATEGORY, 'update');
    }

    trackCategoryDelete() {
        return this.trackEvent(CATEGORY_CATEGORY, 'delete');
    }

    trackTransactionTransferCreate(transaction, from) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'create-transfer', `From ${from}`);
    }

    trackTransactionEventCreate(transaction, from) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'create-event', `From ${from}`);
    }

    trackTransactionBalanceUpdateCreate(transaction, from) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'create-balance-update', `From ${from}`);
    }

    trackTransactionDelete(transaction, from) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'delete', `From ${from}`);
    }

    trackTransactionLabelSuggestionAutocomplete() {
        return this.trackEvent(CATEGORY_TRANSACTION, 'label-autocomplete');
    }

    trackTransactionAutocategoryOverride(transaction, overriddenCategory) {
        let note = transaction.note || '';
        let category = transaction.category ? transaction.category.name || '' : '';
        overriddenCategory = overriddenCategory ? overriddenCategory.name || '' : '';

        return this.trackEvent(CATEGORY_TRANSACTION, 'category-override', `${note}: ${overriddenCategory} -> ${category}`, null, {
            'label': note,
            'overridden_category': overriddenCategory,
            'new_category': transaction.category ? transaction.category.name : ''
        });
    }

    trackTransactionMove() {
        return this.trackEvent(CATEGORY_TRANSACTION, 'move');
    }

    trackTransactionSignChange(transaction) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'change-sign', transaction.type);
    }

    trackTransactionTransferCalendarSwitch() {
        return this.trackEvent(CATEGORY_TRANSACTION, 'switch-transfer-calendars');
    }

    trackTransactionTypeChange(type) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'change-type', type);
    }

    trackTransactionConfirmChange(confirmed) {
        return this.trackEvent(CATEGORY_TRANSACTION, 'change-confirm', confirmed ? 'Confirm' : 'Unconfirm');
    }

    trackTransactionSearch() {
        return this.trackEvent(CATEGORY_TRANSACTION, 'search');
    }

    trackProfilePasswordChange() {
        return this.trackEvent(CATEGORY_PROFILE, 'change-password');
    }

    trackDashboardSelectIncome() {
        return this.trackEvent(CATEGORY_DASHBOARD, 'select-type', 'Income');
    }

    trackDashboardSelectExpense() {
        return this.trackEvent(CATEGORY_DASHBOARD, 'select-type', 'Expense');
    }

    trackDashboardToggleCategoryGroup() {
        return this.trackEvent(CATEGORY_DASHBOARD, 'click-category-group');
    }

    trackDashboardClickEventGroup() {
        return this.trackEvent(CATEGORY_DASHBOARD, 'click-event-group');
    }

    trackPlanSubscribeFromSidebar() {
        return this.trackEvent(CATEGORY_SUBSCRIPTION, 'subscribe', 'From sidebar');
    }

    trackPlanRenewFromSidebar() {
        return this.trackEvent(CATEGORY_SUBSCRIPTION, 'renew', 'From sidebar');
    }

    trackRenewNotificationRenew() {
        return this.trackEvent(CATEGORY_SUBSCRIPTION, 'renew', 'From notification');
    }

    trackRenewNotificationSelectDefaultCalendar() {
        return this.trackEvent(CATEGORY_SUBSCRIPTION, 'select-default-calendar', 'From notification');
    }

    trackModalDismissByBackdrop() {
        return this.trackEvent(CATEGORY_MODAL, 'dismiss', 'Backdrop');
    }

    trackModalDismissByShortcut() {
        return this.trackEvent(CATEGORY_MODAL, 'dismiss', 'Shortcut');
    }

    trackModalDismissByTrigger() {
        return this.trackEvent(CATEGORY_MODAL, 'dismiss', 'Triggered');
    }
}


// WEBPACK FOOTER //
// ./src/common/analytics/google-analytics.js